import { ReactNode } from 'react';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import posthogLib from 'posthog-js';

import httpInstances from '@/api/axiosInstance';
import { Conversation } from '@/features/AIChat/types/chat';
import { getValueOrDefault } from '@/utils/getValueOrDefault';

const CONFIG_NAME = 'userConfig';
export type Option = {
  label: string;
  value: string;
  info?: ReactNode;
};
export type UserConfig = {
  timezone: Option;
  dateFormat: Option;
  defaultMarket: Option;
  homepage: Option;
  chatHistory: Conversation[];
};

type UserConfigState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
  userConfig: UserConfig;
};

type ErrorResponse = {
  message: string;
};
type Request = {
  userConfig: UserConfig;
};

const initialState: UserConfigState = {
  status: 'idle',
  error: null,
  userConfig: {
    timezone: { label: 'UTC', value: 'UTC' },
    dateFormat: { label: '14-Mar-25', value: 'dd-MMM-yy' },
    defaultMarket: { label: '', value: '' },
    homepage: { label: '', value: '' },
    chatHistory: []
  }
};

export const getUserConfig = createAsyncThunk('getUserConfig', async (_, { rejectWithValue }) => {
  try {
    const { data } = await httpInstances.axiosInternalInstance.get(`/config/gxgo/${CONFIG_NAME}`);
    const defaultMarket = data.defaultMarket ?? data.defaultInsight;
    const homepage = data?.homepage?.value?.length ? data.homepage : defaultMarket;
    const chatHistory = data?.chatHistory ?? [];
    return { ...data, defaultMarket, homepage, chatHistory };
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      const errorResponse = error.response.data;
      return rejectWithValue(errorResponse);
    }
    throw error;
  }
});

export const saveUserConfig = createAsyncThunk(
  'saveUserConfig',
  async (request: Request, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const userEmail = state.tripToken.userEmail;
      const [postResponse, patchResponse] = await postUserConfig(request, userEmail);

      if (postResponse.status === 200 && patchResponse.status === 200) {
        posthogLib.capture('$set', {
          $set: {
            defaultMarket: request.userConfig.defaultMarket.label,
            homepage: request.userConfig.homepage.label
          }
        });
        return request.userConfig;
      }
      return rejectWithValue("Couldn't save config file");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue(errorResponse);
      }
      throw error;
    }
  }
);
const userConfigSlice = createSlice({
  name: CONFIG_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getUserConfig.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getUserConfig.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userConfig = { ...state.userConfig, ...action.payload };
      })
      .addCase(getUserConfig.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          state.error = (action.payload as ErrorResponse).message || 'Request failed';
        } else {
          state.error = getValueOrDefault(action.error.message, 'Request failed');
        }
      })
      .addCase(saveUserConfig.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userConfig = { ...state.userConfig, ...action.payload };
      })
      .addCase(saveUserConfig.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          state.error = (action.payload as ErrorResponse).message || 'Request failed';
        } else {
          state.error = getValueOrDefault(action.error.message, 'Request failed');
        }
      });
  }
});

export default userConfigSlice.reducer;

const postUserConfig = async (request: Request, userEmail: string) => {
  const temp = { [CONFIG_NAME]: request.userConfig };

  return Promise.all([
    httpInstances.axiosInternalInstance.post('/config/gxgo', temp),
    httpInstances.axiosGoInstance.patch(`/users/${userEmail}`, {
      prefMarket: request.userConfig.homepage.value
    })
  ]);
};
